import styled, { css } from 'styled-components';
import { Link, NavLink } from 'react-router-dom';

import { Text } from '../../../../components/Text/Text';
import { ANIM, COLORS, LAYOUT_CHROME } from '../../../../theme/tokens';

/* Template `.oh-side-menu` — fixed desktop rail. Holds the live column (`Inner`)
   plus a hidden, out-of-flow `Measure` clone the overflow logic reads heights
   from. Not a flex container itself, so the absolutely-positioned `Measure`
   doesn't disturb the live layout. */
export const Root = styled.nav<{ $immersive?: boolean }>`
  position: fixed;
  /* Drop below the support-impersonation strip when present (--ows-imp-offset is its
     measured height, 0 otherwise). The rail is fixed to the viewport, so it must
     offset itself rather than inherit the layout's. */
  top: calc(24px + var(--ows-imp-offset, 0px));
  bottom: 24px;
  height: calc(100% - 48px - var(--ows-imp-offset, 0px));

  width: ${LAYOUT_CHROME.sideMenuWidth};
  padding: 24px 12px;

  border-radius: 16px;
  background: ${LAYOUT_CHROME.sideMenuBg};

  /* Immersive (full-screen) mode: slide the fixed rail off the left edge. The column that reserved its
     84px collapses in step (see MainLayout SideMenuCol), and a back button restores everything. */
  transition:
    transform 0.28s ease,
    opacity 0.28s ease;
  ${(p) =>
    p.$immersive &&
    css`
      transform: translateX(calc(-100% - 24px));
      opacity: 0;
      pointer-events: none;
    `}
`;

/* The live rail column. Fills Root's content box so its height is viewport-
   driven (independent of how many items we render) — which is what makes the
   overflow measurement stable / non-oscillating. */
export const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`;

/* Hidden measurement clone — the full item list in its collapsed-mode form
   (group separators, no flex spacer) plus a sample "More" trigger. The overflow
   logic reads each item's offset to decide how many fit. `position: absolute`
   makes it the offset parent and keeps it out of the live flow; `visibility:
   hidden` keeps it un-focusable and unpainted while still laid out. */
export const Measure = styled.div`
  position: absolute;
  top: 24px;
  left: 12px;
  width: 60px;
  height: auto;

  display: flex;
  flex-direction: column;
  align-items: center;

  visibility: hidden;
  pointer-events: none;
`;

/* Template `.item` (shared by the logo block, the nav links and the More trigger). */
const itemCss = `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  padding: 8px;
  gap: 8px;

  cursor: pointer;
  user-select: none;

  color: ${LAYOUT_CHROME.sideMenuItem};
  text-align: center;
  font-size: 9px;
  font-weight: 450;
  text-decoration: none;
`;

/* The brand mark is a link home (template `.item.menu`). Rendered as `styled(Link)`
   — same box as the nav items (so the hidden Measure clone's geometry is unchanged)
   while `itemCss`'s `cursor: pointer` / `text-decoration: none` make it read as the
   clickable logo. */
export const LogoItem = styled(Link)`
  ${itemCss}
  margin-bottom: 48px;

  /* Locked rail: the logo stays fully visible but is inert — no home link while the
     owner is still inside onboarding (see SideMenu \`locked\`). */
  &.locked {
    pointer-events: none;
    cursor: default;
  }
`;

export const Item = styled(NavLink)`
  ${itemCss}
  /* The rail's full width, whatever its label (the user, 2026-10-21): every entry as easy to hit. */
  align-self: stretch;

  &.active,
  &:hover {
    color: ${LAYOUT_CHROME.sideMenuItem};

    .txt {
      color: ${LAYOUT_CHROME.navActive};
    }

    path[fill] {
      fill: ${LAYOUT_CHROME.navActive};
    }

    path[stroke] {
      stroke: ${LAYOUT_CHROME.navActive};
    }

    /* Mask-based icons recolour via background-color (glyph components above keep
       the path fill/stroke recolour). */
    .icon-mask {
      background-color: ${LAYOUT_CHROME.navActive};
    }
  }

  /* Onboarding-locked rail (see SideMenu \`locked\`): the entry is inert (no nav,
     no hover/active recolour), its icon is frosted under the padlock LockOverlay,
     and its label dims to 30%. The logo block is never given this class. */
  &.locked {
    pointer-events: none;
    cursor: default;

    .icon {
      position: relative;
    }

    .txt {
      opacity: 0.3;
    }
  }
`;

/* Non-interactive twin of `Item` for the hidden Measure clone — same box (so
   heights match exactly) without NavLink's required `to`. */
export const MeasureItem = styled.div`
  ${itemCss}
`;

/* Anchor for the flyout — the "More" trigger plus its absolutely-positioned
   popup, which opens to the right of the rail. */
export const MoreWrap = styled.div`
  position: relative;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* The "More" trigger. Same shape as a nav item, but a button — its `color`
   drives both the dot glyph (currentColor) and the label (inherited), so open /
   hover / active recolour to the nav accent without the per-path rules the
   fetched icons need. `$active` is set when the current route is one of the
   items hidden inside the flyout, so More reads as selected just like a rail
   item would. */
export const MoreTrigger = styled.button<{ $open: boolean; $active?: boolean }>`
  ${itemCss}
  align-self: stretch;
  appearance: none;
  background: transparent;
  border: 0;
  font-family: inherit;

  color: ${({ $open, $active }) =>
    $open || $active ? LAYOUT_CHROME.navActive : LAYOUT_CHROME.sideMenuItem};
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${LAYOUT_CHROME.navActive};
  }

  /* Locked rail: same treatment as a locked nav Item (inert, frosted icon, dim
     label) — see SideMenu \`locked\`. */
  &.locked {
    pointer-events: none;
    cursor: default;
    color: ${LAYOUT_CHROME.sideMenuItem};

    .icon {
      position: relative;
    }

    .txt {
      opacity: 0.3;
    }
  }
`;

/* Onboarding-locked rail: covers a single nav icon, frosting it (8px backdrop
   blur) under a 50% theme scrim, with the padlock centred on top. Anchored by the
   parent `.icon` (set `position: relative` under `.locked`); the rail logo never
   gets one, so it stays fully visible. The scrim + padlock ink are themed
   (black/near-white on dark, white/deep-ink on light). */
export const LockOverlay = styled.span`
  position: absolute;
  /* Overhang the 24px icon box by 3px each side (→ 30px, +25%) so the frost fully
     covers the wider glyphs (e.g. Players) that fill their box edge-to-edge. */
  inset: -3px;
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 6px;
  /* Padding inside the frosted box so the (small) padlock keeps clear breathing
     room from the blur container's edges. */
  padding: 4px;
  background: ${LAYOUT_CHROME.lockScrim};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: ${LAYOUT_CHROME.lockIcon};

  /* The padlock glyph at 12px (half the 24px nav-icon size). The frosted
     container keeps its size so it still covers the icon beneath. Doubling the
     class via && raises specificity above the parent ItemIcon svg rule, which
     otherwise wins on source order and rendered the padlock full-size. */
  && svg {
    display: block;
    width: 12px;
    height: 12px;
  }
`;

/* Template `.icon` — the inline-SVG slot. */
export const ItemIcon = styled.span`
  display: block;
  position: relative;
  width: 24px;
  height: 24px;

  svg {
    display: block;
    width: 24px;
    height: 24px;
  }

  .icon-mask {
    width: 24px;
    height: 24px;
    background-color: ${LAYOUT_CHROME.navIconIdle};
  }

  .m-bg {
    fill: white;
    fill-opacity: 0.32;
  }
`;

/** Alert dot on a nav icon's top-right corner — flags a rail entry that needs attention
 *  (e.g. a Product feature with a payment to finish). The ring in the rail background keeps
 *  it reading as a badge against the icon beneath. */
export const NavDot = styled.span`
  position: absolute;
  top: -2px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${COLORS.statusDanger};
  box-shadow: 0 0 0 2px ${COLORS.pageBg};
`;

/* Brand mark wrapper — the logo is now an inline SVG (was a remote `<img>`), so
   it recolours with the theme via `currentColor`: near-white heading ink on the
   dark rail, deep ink on the light theme's white rail. Sized off the logo's own
   110:128 ratio (a touch taller than the 24px nav icons). */
export const LogoGlyph = styled.span`
  display: block;
  color: ${COLORS.heading};

  svg {
    display: block;
    width: 24px;
    height: 28px;
  }
`;

/** The Newton N, shown in place of `LogoGlyph` while the owner's `n_showcase`
 *  preference is on. A raster mark with its own metallic shading, so it is not
 *  recoloured by the theme. Same 28px height as the bars it replaces. */
export const NewtonMark = styled.img`
  display: block;
  width: auto;
  height: 28px;
`;

export const ItemLabel = styled(Text).attrs({ tag: 'span' as const })`
  /* colour comes from the parent item / its active state (template .txt) */
`;

export const Sep = styled.div`
  width: 60px;
  height: 1px;
  flex: 0 0 1px;
  background: ${LAYOUT_CHROME.sideMenuSep};
  margin: 24px 0;
`;

export const Spacer = styled.div`
  height: 100%;
  flex: 1;
`;
